/** @jsxImportSource @emotion/react */
import React, {useMemo} from 'react'
import moment from 'moment-timezone'
import { round, trailingZero } from 'utils'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { TooltipList, TooltipListItem, TooltipHeader } from './Tooltip'
import Legend from './Legend'
import { useSeries } from 'hooks'
import _ from 'lodash'

const SERIES = {
  'geoMwh': {
    label: 'Geothermal',
    color: '#CBE612',
    position: 0,
  },
  'cogMwh': {
    label: 'Co-Gen',
    color: '#00a9a5',
    position: 1,
  },
  'gasMwh': {
    label: 'Gas',
    color: '#C6C6C6',
    position: 2,
  },
  'cgMwh': {
    label: 'Coal',
    color: '#ED8B00',
    position: 3,
  },
  'hydMwh': {
    label: 'Hydro',
    color: '#8552AF',
    position: 4,
  },
  'winMwh': {
    label: 'Wind',
    color: '#B1E9E6',
    position: 5,
  },
  'solMwh': {
    label: 'Solar',
    color: '#FFD52D',
    position: 6,
  },
  'liqMwh': {
    label: 'Diesel/Oil',
    color: '#CF0059',
    position: 7,
  },
  'batMwh': {
    label: 'Battery',
    color: '#6388d7',
    position: 8,
  },
}

const SERIES_OPTIONS = {
  xTicks: false,
  tooltipTreshold: false,
}

const formatTimestamp = (timestamp, interval, tooltip=false) => {
  const m = moment(timestamp, 'x')
  switch(interval){
    case 'daily':
      return tooltip ? m.format('DD/MM/YYYY') : m.format('DD MMM')
    case 'monthly':
      return tooltip ? m.format('MMMM YYYY') : m.format('MMM')
    default:
      return tooltip ? m.format('HH:mm DD/MM/YYYY') : m.format('HH:mm')
  }
}

const CustomTooltip = ({ active, series, payload, interval }) => {
  if (active && payload?.length && series?.length){
    const {timestamp, ...data} = payload[0].payload ?? {}
    return (
      <TooltipList>
        <TooltipHeader label={formatTimestamp(timestamp, interval, true)}/>
        { series.map((g, idx) => {
            const {label, color} = SERIES[g]
            const volume = data[g]
            const price = data[g.replace('Mwh', 'Wap')]
            return volume !== undefined ? (
              <TooltipListItem
                key={idx}
                label={label}
                color={color}
                value={`${new Intl.NumberFormat('en').format(round(volume))} MWh`}
                secondaryLabel="Price"
                secondaryValue={`$${trailingZero(price)}`}
              />
          ) : null })
        }
      </TooltipList>
    )
  }
}

// Free version of the widget
// Format data returned by API /free/price
const formatGenerationFree = data => (data?.items || []).map(({tradingDate, generationType}) => ({
  ...(generationType || []).reduce((acc, curr) => ({...acc, ...curr}), {}),
  timestamp: moment(tradingDate).valueOf()
}))

// Paid version of the widget
// Format data returned by API /gen_region_price
const formatGeneration = data => {
  const items = data?.dailyGwap || data?.monthlyGwap || data?.tPGwap || []
  return items.map(({tradingDate, totals}) => ({
    ...(totals?.flat() || []).reduce((acc, curr) => ({...acc, [`${curr.generationType?.toLowerCase()}${_.capitalize(curr.unit)}`]: curr.value}), {}),
    timestamp: moment(tradingDate).valueOf()
  }))
}

const StackedGeneration = ({data, source, interval='daily', width='100%', height='100%', zoomCallback}) => {
  // reformat the API data to something we can chart
  const generationData = useMemo(
    () => source === 'generation' ? formatGeneration(data) : formatGenerationFree(data),
    [JSON.stringify(data), source])

  const [graph, actions] = useSeries(SERIES, generationData, {...SERIES_OPTIONS, zoomCallback})

  return (
    <>
      <ResponsiveContainer width={width} height={height}>
        <BarChart
          throttleDelay={50}
          stackOffset="sign"
          data={graph.data}
          barGap={0}
          barCategoryGap={0}
          margin={interval === 'monthly' ? {left: 20} : {}}
          {...actions.chartProps}
        >
          <XAxis
            tick={{
              fill: 'white',
              fontSize: '14px',
            }}
            dataKey="timestamp"
            domain={graph.xDomain}
            tickFormatter={tick => formatTimestamp(tick, interval)}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{
              fill: 'white',
              fontSize: '14px',
            }}
            tickFormatter={(value) => new Intl.NumberFormat('en').format(value)}
            type="number"
            axisLine={false}
            tickLine={false}
            scale={graph.yScale}
            domain={graph.yDomain}
          />

          <Tooltip
            wrapperStyle={{zIndex: 2}}
            content={args => CustomTooltip({...args, series: graph.visibleSeries, interval})}
          />

          {graph.visibleSeries.toSorted((a, b) => SERIES[a].position - SERIES[b].position).map(g =>
            <Bar
              key={g}
              dataKey={g}
              stackId="1"
              fill={SERIES[g].color}
            />
          )}

          {actions.renderZoomOverlay()}

          <CartesianGrid stroke="#fff" opacity={0.25} strokeDasharray="3 3"/>
        </BarChart>
      </ResponsiveContainer>
      <Legend
        series={SERIES}
        visible={graph.visibleSeries}
        onSeriesToggle={actions.setVisibleSeries}
      />
    </>
  )

}

export default StackedGeneration
